import React from 'react';
import { ModalBase, ModalBaseProps } from '@tanium/react-modal-base';
import { useRandomId } from '@tanium/react-use-random-id';
import { ModalContainer, ModalContainerProps, ModalSize } from './ModalContainer';
import { ModalContent, Props as ModalContentProps } from './ModalContent';

export interface Props extends ModalBaseProps, ModalContentProps {
  /**
   * A custom container used to wrap the standard modal content. If not specified, the standard
   * `ModalContainer` is used.
   */
  container?: React.ComponentType<ModalContainerProps>;
  /**
   * The size of the modal container. May be `sm`, `md`, or `lg`; for the default container,
   * defaults to `md`. If you specify a custom `container`, this prop is passed to the custom
   * container.
   */
  size?: ModalSize;
}

/**
 * A styled modal with optional generic header and footer.
 */
export const Modal = ({
  isOpen,
  onRequestClose,
  onAfterClose,
  size,
  header,
  subheader,
  footer,
  children,
  container: Container = ModalContainer,
}: Props) => {
  const baseId = useRandomId();
  const headerId = `${baseId}-title`;
  const subheaderId = `${baseId}-subtitle`;
  return (
    <ModalBase
      describedById={subheaderId}
      labelledById={headerId}
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      onAfterClose={onAfterClose}
    >
      <Container size={size}>
        <ModalContent
          header={header}
          subheader={subheader}
          footer={footer}
          headerId={headerId}
          subheaderId={subheaderId}
        >
          {children}
        </ModalContent>
      </Container>
    </ModalBase>
  );
};
